import { select } from '@ptl/angular-redux-store';
import { Component } from '@angular/core';
import { AbstractControl, Validators } from '@angular/forms';
import { BaseAgency, EntitlementsHelperService, LINE_OF_BUSINESS_ENUM, LOGIN_INFO_REDUCER_NAME, UserInfoStateStructure } from '@ptl/account-management';
import { FormComponentLookup, FormEntryComponentClass } from '@ptl/forms';
import { Observable } from 'rxjs';
import { ReducerUtilityService as SxeangReducerService } from '../../../../shared/utility-services/reducer-utility.service';
import { saveAgency } from '../../../producer/configs/reducers/producer.reducer';
import { ProducerAgencyHelperService } from '../../../producer/producer-agency-helper.service';
import { PurlFormState, SendReferralFormState } from '../../purl-form/interfaces/purl.interface';
import { patchPurlState, patchSendReferralState, PURL_STATE } from '../../purl-form/reducer/purl.reducer';

@FormComponentLookup('agency-selector-dropdown')
@Component({
    selector: 'app-agency-selector-dropdown',
    templateUrl: './agency-selector-dropdown.component.html',
    styleUrls: ['./agency-selector-dropdown.component.scss'],
    standalone: false
})
export class AgencySelectorDropdownComponent extends FormEntryComponentClass {

  lob: LINE_OF_BUSINESS_ENUM;
  availableAgencies: BaseAgency[];

  @select(['componentStates', 'ZipCounty'])
  zipCounty$: Observable<boolean>;

  constructor(
    private entitlementService: EntitlementsHelperService,
    private localReducer: SxeangReducerService,
    private producerHelper: ProducerAgencyHelperService
  ) { 
    super();
  }

  async ngOnInit() {
    if (this.entry.custom?.lob) {
      this.lob = this.entry.custom.lob;
    }
    else {
      this.lob = await this.entitlementService.getCurrentLobPromise();
    }

    let selectedAgency = this.entitlementService.getSelectedAgency(this.lob);
    this.availableAgencies = this.entitlementService.getAgenciesByLineOfBusiness(this.lob);

    if (selectedAgency) {
      const purlState: PurlFormState = this.localReducer.getPayloadState(PURL_STATE);
      this.resetFormValue(selectedAgency);
      this.ngRedux.dispatch(patchSendReferralState(this.getDependentAgencyValues(selectedAgency)));
    }

    // Update validity if needed when zip code changes
    this.subscriptionGroup.push(this.zipCounty$.subscribe((value: any) => {
      this.formControls[this.entry.formControlAttributes.formControlName].updateValueAndValidity();
    }));
  }

  ngAfterViewInit() {
    const formControl: AbstractControl = this.formControls[this.entry.formControlAttributes.formControlName];
    let validators = [];

    if (this.entry.formControlAttributes.validators?.filter((validator) => validator.type === 'regionMismatch').length) {
      validators.push((control: AbstractControl) => {
        let selectedAgency = control.value;
        let zipCountyState = this.reducerUtilityService.getComponentStates().ZipCounty;
        let isValid = !zipCountyState?.county || !this.producerHelper.isRegionMismatch(selectedAgency, zipCountyState);
    
        return isValid ? null : {'regionMismatch': true};
      });
    }
    
    if (this.entry.formControlAttributes.validators?.filter((validator) => validator.type === 'required').length) {
      validators.push(Validators.required);
    }

    formControl.setValidators(validators);
    formControl.updateValueAndValidity();
    this.subscribeToFormStream(this.entry.base.formListeners);
  }

  agencyChange(agency: BaseAgency) {
    let agencyVals = this.getDependentAgencyValues(agency);

    this.form.controls[this.entry.custom?.agencyNumberFormControlName]?.setValue(agencyVals.agencyNumber);
    this.form.controls[this.entry.custom?.npnFormControlName]?.setValue(agencyVals.agencyNpn);

    // I think this is triggering some sort of state change detection which is overriding the value
    // before it can actually be updated, so a timeout is needed.
    setTimeout(() => {
      this.ngRedux.dispatch(saveAgency(agency));
    }, 100);
  }

  trackAgencyComparison(option: BaseAgency, value: BaseAgency) {
    return option?.agencyId === value?.agencyId && option?.region === value?.region;
  }

  getDependentAgencyValues(agency: BaseAgency): SendReferralFormState {
    let agencyVals = {
      agencyNpn: null,
      agencyNumber: null
    } as SendReferralFormState;

    const loginInfo: UserInfoStateStructure = this.localReducer.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);
    let npnNo = loginInfo.userInfo.npnNo || agency.npnNo || agency.agencyId;
    agency.npnNo = npnNo;

    if (agency) {
      if (this.entry.custom?.npnFormControlName) {
          agencyVals.agencyNpn = npnNo;
      }

      if (this.entry.custom?.agencyNumberFormControlName) {
        agencyVals.agencyNumber = agency.agencyId;
      }
    }

    return agencyVals;
  }
}
